import React from "react";
import { Button } from "@mui/material";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import styles from "./CompanyInvoice.module.scss";
import { BrandlistResponseData, InvoiceContent } from "../../../../apis/models/BrandlistResponse";
import { useNavigate } from "react-router-dom";
import { formatNumber, dateToString } from "../../../../libs/helpers";
import { InvStatus } from "../../../../libs/constants";
import { InvoiceMasterData, InvoiceMasterPUTRequest } from "../../../../apis/models/InvoiceMasterRequest";
import { useDispatch } from "react-redux";
import { saveMasterInfo } from "../../../../apis/reducers/invoiceMasterReducer";
import { format } from "date-fns";
import GeneralDialog from '../../../core/GeneralDialog';
import { invoiceContentProps } from "../../../InvoiceDetailsPage/InvoiceContent/InvoiceContent";
import { callDownloadInvoiceApi, getInvoiceDetails, getInvoiceMaster } from "../../../../apis/invmaster-api";
import InvoiceDownloadDialog from "../../../dialogs/InvoiceDownloadDialog";

const CompanyInvoice = (props: CompanyInvoiceProps) => {
  const { data, onOpenProcessFee, onOpenOfficeWorkFee, onOpenWrappingFee, onChangeInvStatus, afterDownload: onDownloadInv } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openInvoiceConfirmationDialog, setOpenInvoiceConfirmationDialog] = React.useState<boolean>(false);
  const [downloadData, setDownloadData] = React.useState<{open: boolean; invoice?: InvoiceMasterData}>({open: false, invoice: undefined});

  const formatInvReq = () => {
    const output: InvoiceMasterPUTRequest = {
      status: InvStatus.CONFIRMED,
    };

    return output;
  }

  const handleChangeRoute = (inv: InvoiceContent) => {
    const info = {
      brandName: data.brandName, 
      brandNo: inv.brandNo,
      status: inv.status,
      totalPrice: inv.totalPrice,
      from: format(new Date(inv.from), 'yyyy/MM/dd'),
      to: format(new Date(inv.to), 'yyyy/MM/dd'),
      id: inv.invoiceMasterId
    }
    dispatch(saveMasterInfo(info))
    navigate(`/invoice/${inv.invoiceMasterId}`)
  }

  const handleChangeRouteBrandDetails = (data: BrandlistResponseData) => {
    const info = {
      brandName: data.brandName,
      brandNo: data.brandNo,
      cutoffDate: data.cutoffDate,
      staffName: data.salesStaff?.userName ?? "-"
    }
    navigate(`/brand/${data.brandNo}`, {state: info})
  }

  const handleClickDownloadInvoice = async (inv: InvoiceContent) => {
    const response = await getInvoiceMaster(inv.invoiceMasterId)
    const invoiceData = response.data.data as InvoiceMasterData
    setDownloadData({
      open: true,
      invoice: invoiceData
    })
  }

  const downloadInvoice = async (inv: InvoiceMasterData, pdf: boolean, excel: boolean) => {
    setDownloadData({
      open: false,
      invoice: undefined
    })

    const response = await callDownloadInvoiceApi(inv.invoiceMasterId, pdf, excel)
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `請求書_ブランド_${inv.brandNo}_${inv.year}${String(inv.month).padStart(2, '0')}.zip`);
    document.body.appendChild(link);
    link.click();

    inv.status = InvStatus.EXPORT
  }

  return (
    <div>
      <div className={styles.card_holder}>
        <div className={styles.card_content}>
          <div className={styles.card_content_header}>
            <div className={styles.card_content_header_info}>
              <span className={styles.blueheader} onClick={() =>  handleChangeRouteBrandDetails(data)}>
                {data.brandName}
              </span>
              <span>No. {data.brandNo}</span>
              <span className={styles.brand_status}>{data.cutoffDate === 31 ? "月末請求" : data.cutoffDate}</span>
              <span>担当者 : {data.salesStaff?.userName ?? "-"}</span>
            </div>
            <div className={styles.sameRow}>
              <Button variant="outlined" style={{ maxHeight: "33px" }} onClick={() => { onOpenProcessFee(data) }}>
                加工費入力
              </Button>
              <Button variant="outlined" style={{ maxHeight: "33px" }} onClick={() => { onOpenOfficeWorkFee(data) }}>
                事務費入力
              </Button>
              <Button variant="outlined" style={{ maxHeight: "33px" }} onClick={() => { onOpenWrappingFee(data) }}>
                資材費入力
              </Button>
            </div>
          </div>
          <div className={styles.divider_container}>
            <div className={styles.divider_border} />
          </div>
          {Array.isArray(data.invoices) &&
            data.invoices.length > 0 &&
            data.invoices.sort((a,b) => new Date(b.from).getTime() - new Date(a.from).getTime()).map((inv, index) => ( index < 2 &&
              <div className={styles.flexContainer} key={index}>
                <div className={styles.left_info}>
                  <div>
                    <span>合計</span>
                    <span className={styles.large_number_text}>{formatNumber(inv.totalPrice)}</span>
                    <span>円 (税込)</span>
                  </div>
                  <div>
                    <span>{dateToString(inv.from)}</span>
                    <span>~</span>
                    <span>{dateToString(inv.to)}</span>
                  </div>
                  <div className={styles.left_info_status}>
                  {
                    inv.status === InvStatus.NOT_CONFIRMED ? <span>未確定</span> 
                    : inv.status === InvStatus.CONFIRMED ? <span>確定済</span> 
                    : inv.status === InvStatus.EXPORT && <span>請求書発行済</span>
                  }
                  {(inv.status === InvStatus.CONFIRMED || inv.status === InvStatus.EXPORT) && (
                      <Button
                        variant="contained"
                        color="secondary"
                        style={{ maxHeight: "33px", fontSize: "12px" }}
                        onClick={() => handleClickDownloadInvoice(inv)}
                      >
                        請求書ダウンロード
                      </Button>
                    )}
                    {inv.status === InvStatus.NOT_CONFIRMED && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        style={{ maxHeight: "33px", fontSize: "12px" }}
                        onClick={() => setOpenInvoiceConfirmationDialog(true)}
                      >
                        請求確定
                      </Button>
                    )}
                    <GeneralDialog
                      open={openInvoiceConfirmationDialog} 
                      onCancel={() => setOpenInvoiceConfirmationDialog(false)} 
                      cancelText="編集に戻る" 
                      onSave={() => {onChangeInvStatus(inv.invoiceMasterId, formatInvReq()); setOpenInvoiceConfirmationDialog(false)}} 
                      saveText="請求確定" 
                      title="請求確定" 
                      onClose={() => setOpenInvoiceConfirmationDialog(false)}
                      >
                      <div>
                        下記の請求を確定しますか？<br/><br/>
                        {data.brandName}<br/>
                      </div>
                      <div>{`請求期間：${dateToString(inv.from)} ~ ${dateToString(inv.to)}`}</div>
                    </GeneralDialog>
                    {(downloadData.open && downloadData.invoice) &&
                      <InvoiceDownloadDialog
                        targetInvoice={downloadData.invoice}
                        open={true}
                        onClose={() => setDownloadData({open: false, invoice: undefined})}
                        onDownload={(invoice, pdf, excel) => downloadInvoice(invoice, pdf, excel)}
                      />
                    }
                  </div>
                </div>
                <div className={styles.rightText} onClick={() => handleChangeRoute(inv)}>
                  <span>詳細を見る</span>
                  <KeyboardArrowRightRoundedIcon color="primary" />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default CompanyInvoice;

export type CompanyInvoiceProps = {
  data: BrandlistResponseData,
  onOpenProcessFee: (brand: BrandlistResponseData) => void,
  onOpenOfficeWorkFee: (brand: BrandlistResponseData) => void,
  onOpenWrappingFee: (brand: BrandlistResponseData) => void,
  afterDownload: () => void,
  onChangeInvStatus: (id: string, inv: InvoiceMasterPUTRequest) => void
};
